<template>
  <div>
    <div v-if="status === 'loading'" class="text-muted small" role="status">{{ loadingMessage }}</div>
    <div v-if="status === 'error'" class="text-danger small" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn btn-link btn-sm" @click.stop="load">Try again</button>
    </div>
    <img
      v-if="imageUrl"
      :key="imageUrl"
      :src="imageUrl"
      :class="imageClass"
      :alt="alt"
      @load="imageLoaded"
      referrerpolicy="no-referrer"
      @error="imageFailed"
    />
  </div>
</template>

<script>
import { watchApiSession } from '@/api';

export default {
  props: {
    loadAccess: {
      type: Function,
      required: true
    },
    imageClass: String,
    alt: {
      type: String,
      default: 'File preview'
    },
    loadingMessage: {
      type: String,
      default: 'Loading image…'
    },
    errorMessage: {
      type: String,
      default: 'Could not load the image.'
    }
  },
  emits: ['loaded', 'error'],
  data() {
    return {
      imageUrl: null,
      status: 'loading',
      requestVersion: 0,
      controller: null,
      timer: null,
      pending: null,
      resolveLoaded: null,
      rejectLoaded: null,
      stopSession: null,
      disposed: false
    };
  },
  watch: {
    loadAccess: {
      immediate: true,
      handler() {
        this.load();
      }
    }
  },
  async mounted() {
    try {
      const stop = await watchApiSession(() => this.load());

      if (this.disposed) {
        stop();
      } else {
        this.stopSession = stop;
      }
    } catch {
      if (!this.disposed) {
        this.fail();
      }
    }
  },
  beforeUnmount() {
    this.disposed = true;
    this.stopSession?.();
    this.cancelLoad();
  },
  methods: {
    /** Remove temporary links and stop stale requests when the record, account or component changes. */
    cancelLoad() {
      this.requestVersion += 1;
      this.controller?.abort();
      clearTimeout(this.timer);
      this.rejectLoaded?.(new Error('Image loading was cancelled.'));
      this.resolveLoaded = null;
      this.rejectLoaded = null;
      this.pending = null;

      this.imageUrl = null;
    },

    /** Request a signed URL and wait for Storage to deliver a decodable image before printing. */
    async load() {
      if (this.disposed) {
        return;
      }

      this.cancelLoad();
      const version = this.requestVersion;
      this.status = 'loading';
      this.controller = new AbortController();
      this.pending = new Promise((resolve, reject) => {
        this.resolveLoaded = resolve;
        this.rejectLoaded = reject;
      });

      // Previews have no caller awaiting readiness; printing can still await the original promise.
      this.pending.catch(() => {});
      this.timer = setTimeout(() => this.fail(), 30000);

      try {
        const access = await this.loadAccess({
          signal: this.controller.signal
        });

        if (version !== this.requestVersion || this.status !== 'loading') {
          return;
        }

        this.imageUrl = access.url;
      } catch {
        if (version === this.requestVersion && this.status === 'loading') {
          this.fail();
        }
      }
    },

    /** Resolve print readiness only after this image has loaded successfully. */
    imageLoaded(event) {
      if (this.status !== 'loading' || event.target.src !== this.imageUrl) {
        return;
      }

      clearTimeout(this.timer);
      this.status = 'loaded';
      this.resolveLoaded?.();
      this.resolveLoaded = null;
      this.rejectLoaded = null;
      this.$emit('loaded');
    },

    /** Ignore events from replaced images; retry actions always obtain a fresh signed URL. */
    imageFailed(event) {
      if (event.target.src === this.imageUrl) {
        this.fail();
      }
    },

    /** Keep failed or timed-out evidence out of print output and provide a retry action. */
    fail() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.status = 'error';
      this.rejectLoaded?.(new Error(this.errorMessage + ' Please try again.'));
      this.resolveLoaded = null;
      this.rejectLoaded = null;

      this.imageUrl = null;

      this.$emit('error');
    },

    /** Let print callers await both the authenticated request and the image load. */
    async waitUntilReady() {
      if (!this.pending) {
        throw new Error('Image is unavailable.');
      }

      const version = this.requestVersion;
      await this.pending;

      if (version !== this.requestVersion || this.status !== 'loaded') {
        throw new Error('Image changed while preparing to print. Please try again.');
      }
    }
  }
};
</script>

<style scoped>
img {
  display: block;
  max-width: 100%;
  max-height: 75vh;
  margin: auto;
  object-fit: contain;
}
</style>
